import { useState, type FormEvent } from "react";
import { Icon } from "../icons/Icon";
import { Aviso } from "../components/Layout";
import { api, cpfValido, fmt, type Edital, type ErroApi } from "../lib/api";

type Protocolo = { protocolo: string; mensagem: string; criadoEm: string };

/**
 * Impugnação ao edital (Anexo IV, item 3).
 *
 * Aberta a qualquer cidadão, com ou sem inscrição (item 3.1). O campo "item
 * impugnado" é obrigatório porque o item 3.2 prevê o não conhecimento da
 * impugnação que não o indique.
 */
export function Impugnacao({ edital }: { edital: Edital }) {
  const fase = edital.cronograma.find((f) => f.codigo === "IMPUGNACAO");
  const aberta = fase?.aberta ?? false;

  const [form, setForm] = useState({
    nome: "", cpf: "", rg: "", email: "", telefone: "", endereco: "",
    itemImpugnado: "", justificativa: "",
  });
  const [anexo, setAnexo] = useState<File | null>(null);
  const [arraste, setArraste] = useState(false);
  const [erros, setErros] = useState<string[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [feito, setFeito] = useState<Protocolo | null>(null);

  function atualizar(mudanca: Partial<typeof form>) {
    setForm((f) => ({ ...f, ...mudanca }));
  }

  async function enviar(ev: FormEvent) {
    ev.preventDefault();
    const problemas: string[] = [];
    if (!form.nome.trim()) problemas.push("Informe o seu nome completo.");
    if (!cpfValido(form.cpf)) problemas.push("Informe um CPF válido, com 11 dígitos.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) problemas.push("Informe um e-mail válido.");
    if (!form.itemImpugnado.trim()) {
      problemas.push("Indique o item ou subitem impugnado: sem essa indicação a impugnação não é conhecida.");
    }
    if (form.justificativa.trim().length < 20) {
      problemas.push("Descreva a razão da impugnação com pelo menos 20 caracteres.");
    }
    setErros(problemas);
    if (problemas.length) return;

    setEnviando(true);
    try {
      const dados = new FormData();
      Object.entries(form).forEach(([k, v]) => dados.set(k, v));
      dados.set("cpf", form.cpf.replace(/\D/g, ""));
      if (anexo) dados.set("anexo", anexo);
      setFeito(await api.postForm<Protocolo>("/api/publico/impugnacoes", dados));
      window.scrollTo({ top: 0 });
    } catch (e) {
      const err = e as ErroApi;
      setErros(err.motivos.length ? err.motivos : [err.mensagem]);
    } finally {
      setEnviando(false);
    }
  }

  if (feito) {
    return (
      <section className="secao">
        <div className="container container-estreito pilha pilha-6">
          <h1>Impugnação recebida</h1>
          <div className="folha-token">
            <p className="rotulo-sobre">Número do protocolo</p>
            <p className="token-numero mono" style={{ fontSize: "clamp(1.5rem, 6vw, 2.25rem)" }}>
              {feito.protocolo}
            </p>
          </div>
          <p className="texto-secundario">{feito.mensagem}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="secao">
      <div className="container container-estreito pilha pilha-6">
        <div className="pilha pilha-2">
          <h1>Impugnar o edital</h1>
          <p className="texto-secundario">
            Qualquer cidadão pode impugnar fundamentadamente este edital ou suas alterações.
            Não é preciso ter inscrição.
          </p>
        </div>

        {!aberta && (
          <Aviso tipo="atencao" titulo="Fora do prazo de impugnação">
            {fase
              ? <>O período de impugnação é de <strong>{fase.periodoFormatado}</strong>.</>
              : "O período de impugnação não está configurado neste edital."}
          </Aviso>
        )}

        <Aviso tipo="info">
          Os pedidos são julgados pela Comissão Especial Avaliadora e as respostas publicadas em
          arquivo único no site da Prefeitura. Da decisão sobre a impugnação não cabe recurso
          administrativo.
        </Aviso>

        {erros.length > 0 && (
          <div className="resumo-erros" role="alert" tabIndex={-1}>
            <h2>Há {erros.length === 1 ? "um problema" : `${erros.length} problemas`} a corrigir</h2>
            <ul>{erros.map((e, i) => <li key={i}>{e}</li>)}</ul>
          </div>
        )}

        <form onSubmit={enviar} className="cartao pilha" noValidate>
          <div className="campo">
            <label className="campo-rotulo" htmlFor="nome">Nome completo</label>
            <input id="nome" className="entrada" value={form.nome}
                   onChange={(e) => atualizar({ nome: e.target.value })} />
          </div>

          <div className="grade grade-2">
            <div className="campo">
              <label className="campo-rotulo" htmlFor="cpf">CPF</label>
              <input id="cpf" className="entrada" inputMode="numeric" value={form.cpf}
                     onChange={(e) => atualizar({ cpf: fmt.cpf(e.target.value) })} />
            </div>
            <div className="campo">
              <label className="campo-rotulo" htmlFor="rg">RG</label>
              <input id="rg" className="entrada" value={form.rg}
                     onChange={(e) => atualizar({ rg: e.target.value })} />
            </div>
          </div>

          <div className="grade grade-2">
            <div className="campo">
              <label className="campo-rotulo" htmlFor="email">E-mail</label>
              <input id="email" type="email" className="entrada" value={form.email}
                     onChange={(e) => atualizar({ email: e.target.value })} />
            </div>
            <div className="campo">
              <label className="campo-rotulo" htmlFor="telefone">Telefone</label>
              <input id="telefone" className="entrada" inputMode="tel" value={form.telefone}
                     onChange={(e) => atualizar({ telefone: fmt.telefone(e.target.value) })} />
            </div>
          </div>

          <div className="campo">
            <label className="campo-rotulo" htmlFor="endereco">Endereço</label>
            <input id="endereco" className="entrada" value={form.endereco}
                   onChange={(e) => atualizar({ endereco: e.target.value })} />
          </div>

          <hr className="regua" />

          <div className="campo">
            <label className="campo-rotulo" htmlFor="item">Item ou subitem impugnado</label>
            <span className="campo-ajuda" id="item-ajuda">
              Exemplo: item 8.9 ou Anexo II, Categoria II. Obrigatório, sob pena de não
              conhecimento.
            </span>
            <input id="item" className="entrada" value={form.itemImpugnado}
                   aria-describedby="item-ajuda"
                   onChange={(e) => atualizar({ itemImpugnado: e.target.value })} />
          </div>

          <div className="campo">
            <label className="campo-rotulo" htmlFor="justificativa">Razão da impugnação</label>
            <span className="campo-ajuda" id="just-ajuda">
              Fundamentação resumida, clara e objetiva.
            </span>
            <textarea id="justificativa" className="area-texto" value={form.justificativa}
                      aria-describedby="just-ajuda"
                      onChange={(e) => atualizar({ justificativa: e.target.value })} />
          </div>

          <div className="campo">
            <label className="campo-rotulo" htmlFor="anexo">Anexo (opcional)</label>
            <span className="campo-ajuda" id="anexo-ajuda">Arquivo em PDF, de até 10 MB.</span>
            <div
              className={`arquivo-drop${arraste ? " arraste-sobre" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setArraste(true); }}
              onDragLeave={() => setArraste(false)}
              onDrop={(e) => {
                e.preventDefault();
                setArraste(false);
                setAnexo(e.dataTransfer.files?.[0] ?? null);
              }}
            >
              <input id="anexo" type="file" accept="application/pdf"
                     aria-describedby="anexo-ajuda"
                     onChange={(e) => setAnexo(e.target.files?.[0] ?? null)} />
              <Icon nome="arquivo" tamanho={24} className="arquivo-drop-icone" />
              <span className="arquivo-drop-rotulo">Arraste o PDF aqui ou clique para escolher</span>
              <span className="arquivo-drop-ajuda">Formato PDF, até 10 MB</span>
            </div>
            {anexo && (
              <div className="arquivo-selecionado">
                <Icon nome="arquivo" tamanho={16} />
                {anexo.name}
                <button type="button" onClick={() => setAnexo(null)} aria-label="Remover arquivo">
                  <Icon nome="cancelar" tamanho={16} />
                </button>
              </div>
            )}
          </div>

          <button type="submit" className="btn btn-primario btn-largo" disabled={enviando || !aberta}>
            {enviando ? <span className="carregando" /> : <Icon nome="arquivo" tamanho={18} />}
            {enviando ? "Enviando" : "Protocolar impugnação"}
          </button>
        </form>
      </div>
    </section>
  );
}
